
// Import necessary dependencies
import { StockUpdate } from '../../types';
import { supabase, getCurrentUserId } from '../../supabase';
import { getStockUpdates, setStockUpdates } from './core';
import { getProducts, setProducts } from '../product/core';
import { logError } from '@/utils/errorLogger';

// Supabase stock update functions
export const fetchStockUpdates = async (): Promise<StockUpdate[]> => {
  try {
    const userId = await getCurrentUserId();
    if (!userId) {
      console.log('No user ID, falling back to localStorage');
      return getStockUpdates();
    }

    // Add timeout to prevent hanging requests
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Request timeout')), 5000)
    );

    const fetchPromise = supabase
      .from('stock_updates')
      .select('*')
      .eq('user_id', userId);

    const { data, error } = await Promise.race([fetchPromise, timeoutPromise]) as any;

    if (error) {
      logError({
        type: 'api',
        message: `Error fetching stock updates: ${error.message}`,
        source: 'fetchStockUpdates',
      });
      console.error('Error fetching stock updates:', error);
      return getStockUpdates(); // Fallback to localStorage
    }

    if (!data) {
      console.log('No data returned, falling back to localStorage');
      return getStockUpdates();
    }

    // Map from database format to app format
    const updates: StockUpdate[] = data.map(item => ({
      id: item.id,
      productId: item.product_id,
      quantity: item.quantity,
      date: new Date(item.date),
    }));

    // Update localStorage cache
    setStockUpdates(updates);
    return updates;
  } catch (error: any) {
    logError({
      type: 'api',
      message: `Error in fetchStockUpdates: ${error.message}`,
      source: 'fetchStockUpdates',
      stack: error.stack,
    });
    console.error('Error in fetchStockUpdates:', error);
    return getStockUpdates(); // Fallback to localStorage
  }
};

export const addStockUpdate = async (update: StockUpdate): Promise<boolean> => {
  try {
    const userId = await getCurrentUserId();
    if (!userId) {
      // Still add to localStorage even if no user is logged in
      const updates = getStockUpdates();
      setStockUpdates([...updates, update]);
      
      // Update the local product stock
      updateLocalProductStock(update);
      
      console.warn('No user ID available, saved to localStorage only');
      return false;
    }

    // First, add to localStorage for offline support
    const updates = getStockUpdates();
    setStockUpdates([...updates, update]);
    
    // Update the local product stock
    updateLocalProductStock(update);

    // Then add to Supabase
    const { error } = await supabase.from('stock_updates').insert({
      id: update.id,
      user_id: userId,
      product_id: update.productId,
      quantity: update.quantity,
      date: update.date.toISOString(),
    });

    if (error) {
      logError({
        type: 'api',
        message: `Error adding stock update to Supabase: ${error.message}`,
        source: 'addStockUpdate',
      });
      console.error('Error adding stock update to Supabase:', error);
      return false;
    }

    // Instead of using RPC, use a direct function to update stock
    try {
      await directUpdateProductStock(update.productId, update.quantity, userId);
      return true;
    } catch (err) {
      console.error('Error updating product stock:', err);
      return false;
    }
  } catch (error: any) {
    logError({
      type: 'api',
      message: `Error in addStockUpdate: ${error.message}`,
      source: 'addStockUpdate',
      stack: error.stack,
    });
    console.error('Error in addStockUpdate:', error);
    return false;
  }
};

// New function to directly update product stock without RPC
const directUpdateProductStock = async (
  productId: string,
  quantity: number,
  userId: string
): Promise<boolean> => {
  try {
    // First get the current stock
    const { data: productData, error: getError } = await supabase
      .from('products')
      .select('current_stock')
      .eq('id', productId)
      .eq('user_id', userId)
      .single();
    
    if (getError) {
      logError({
        type: 'api',
        message: `Error getting product for stock update: ${getError.message}`,
        source: 'directUpdateProductStock',
      });
      console.error('Error getting product for stock update:', getError);
      return false;
    }
    
    const newStock = productData.current_stock + quantity;
    
    const { error: updateError } = await supabase
      .from('products')
      .update({ 
        current_stock: newStock, 
        updated_at: new Date().toISOString() 
      })
      .eq('id', productId)
      .eq('user_id', userId);
      
    if (updateError) {
      logError({
        type: 'api',
        message: `Error in direct product stock update: ${updateError.message}`,
        source: 'directUpdateProductStock',
        stack: updateError.stack,
      });
      console.error('Error in direct product stock update:', updateError);
      return false;
    }
    
    return true;
  } catch (error: any) {
    logError({
      type: 'api',
      message: `Error in directUpdateProductStock: ${error.message}`,
      source: 'directUpdateProductStock',
      stack: error.stack,
    });
    console.error('Error in directUpdateProductStock:', error);
    return false;
  }
};

// Helper function to update local product stock
const updateLocalProductStock = (update: StockUpdate): void => {
  // Update the current stock of the product
  const products = getProducts();
  const productIndex = products.findIndex((p) => p.id === update.productId);
  
  if (productIndex !== -1) {
    const updatedProduct = {
      ...products[productIndex],
      currentStock: products[productIndex].currentStock + update.quantity,
      updatedAt: new Date(),
    };
    
    products[productIndex] = updatedProduct;
    setProducts(products);
  }
};
